import { useState, useEffect } from "react";
import { X, Eye, Save } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { FileUpload } from "./FileUpload";

const CATEGORY_OPTIONS = [
  "Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Innovation & Startups",
  "Lifestyle & Culture", "Inspiration & People", "Gaming", "Digital Culture",
];
const STATUSES = ["Draft", "Active", "Paused", "Ended"];

// Maps each placement key to a mini wireframe variant + plain-language description.
const PLACEMENT_VIS = {
  homepage_sponsor: { v: "feature", d: "Homepage — large sponsor feature panel (left side of the showcase)." },
  homepage_sponsor_card: { v: "sidecard", d: "Homepage — sponsor card on the right of the showcase." },
  homepage_latest_sidebar: { v: "sidebar", d: "Homepage — right sidebar sponsor card next to the Latest stories." },
  homepage_after_6: { v: "grid", d: "Homepage — a sponsored card inside the article grid." },
  hero_latest: { v: "hero", d: "Homepage — right column under the big hero headline." },
  articles_sidebar: { v: "sidebar", d: "Articles page — right-hand desktop sidebar." },
  article_grid: { v: "grid", d: "Articles page — after the 6th article card (mobile)." },
  article_inline: { v: "inline", d: "Inside an article — mid-content, after the 3rd paragraph." },
  article_sidebar: { v: "sidebar", d: "Inside an article — right-hand desktop sidebar." },
  ptiplay: { v: "feed", d: "Arcade page — sponsor card in the games feed." },
  shorts: { v: "vertical", d: "Shorts — sponsor card in the vertical video feed." },
  inkwire: { v: "feed", d: "InkWire — sponsor card in the live news feed." },
};

const RED = "#ef1831", GRAY = "#d1d5db", LITE = "#eef0f2", DARK = "#111827";

function PlacementThumb({ pkey }) {
  const v = (PLACEMENT_VIS[pkey] || {}).v || "feed";
  const P = { width: 60, height: 40, viewBox: "0 0 72 48", className: "shrink-0 rounded-sm border border-gray-200 bg-white" };
  const header = <rect x="0" y="0" width="72" height="6" fill={DARK} />;
  if (v === "sidebar") return (
    <svg {...P}>{header}
      {[14, 21, 28, 35].map((y) => <rect key={y} x="6" y={y} width="40" height="3" fill={LITE} />)}
      <rect x="6" y="12" width="40" height="8" fill={GRAY} />
      <rect x="52" y="12" width="16" height="26" rx="1" fill={RED} />
    </svg>);
  if (v === "hero") return (
    <svg {...P}>{header}
      <rect x="6" y="12" width="38" height="26" fill={GRAY} />
      <rect x="48" y="12" width="20" height="4" fill={LITE} />
      <rect x="48" y="19" width="20" height="4" fill={LITE} />
      <rect x="48" y="27" width="20" height="11" rx="1" fill={RED} />
    </svg>);
  if (v === "grid") return (
    <svg {...P}>{header}
      {[[6, 12], [30, 12], [54, 12], [30, 28], [54, 28]].map(([x, y], i) => <rect key={i} x={x} y={y} width="12" height="12" rx="1" fill={GRAY} />)}
      <rect x="6" y="28" width="12" height="12" rx="1" fill={RED} />
    </svg>);
  if (v === "inline") return (
    <svg {...P}>{header}
      {[12, 18].map((y) => <rect key={y} x="14" y={y} width="44" height="3" fill={LITE} />)}
      <rect x="14" y="24" width="44" height="9" rx="1" fill={RED} />
      <rect x="14" y="37" width="44" height="3" fill={LITE} />
    </svg>);
  if (v === "vertical") return (
    <svg {...P}>{header}
      <rect x="8" y="11" width="18" height="30" rx="1" fill={GRAY} />
      <rect x="30" y="11" width="18" height="30" rx="1" fill={RED} />
      <rect x="52" y="11" width="18" height="30" rx="1" fill={GRAY} />
    </svg>);
  if (v === "feature") return (
    <svg {...P}>{header}
      <rect x="6" y="12" width="42" height="26" rx="1" fill={RED} />
      <rect x="52" y="12" width="16" height="26" rx="1" fill={GRAY} />
    </svg>);
  if (v === "sidecard") return (
    <svg {...P}>{header}
      <rect x="6" y="12" width="42" height="26" rx="1" fill={LITE} />
      <rect x="52" y="12" width="16" height="26" rx="1" fill={RED} />
    </svg>);
  return ( // feed
    <svg {...P}>{header}
      <rect x="6" y="11" width="60" height="7" rx="1" fill={GRAY} />
      <rect x="6" y="20" width="60" height="7" rx="1" fill={RED} />
      <rect x="6" y="29" width="60" height="7" rx="1" fill={GRAY} />
    </svg>);
}

const blank = {
  name: "", partner: "", href: "", image: "", image_alt: "", custom_html: "",
  creative_type: "native", headline: "", description: "", cta: "", logo: "", category: "",
  label: "Advertisement", target: "_blank",
  placements: [], eligible_categories: [], exclude_categories: [], block_sensitive: true,
  start_date: "", end_date: "", status: "Draft", priority: 1, weight: 1, max_impressions: 0, notes: "",
};

export function AffiliateCampaignForm({ open, onClose, onSaved, placements, initial }) {
  const [f, setF] = useState(initial ? { ...blank, ...initial } : blank);
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);
  // Re-hydrate the form whenever it opens or a different campaign is loaded, so edits retain saved info.
  useEffect(() => {
    if (open) {
      setF(initial ? { ...blank, ...initial } : blank);
      setPreviewHtml(initial?.custom_html_safe || "");
    }
  }, [open, initial?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const doPreview = async () => {
    if (f.custom_html) {
      try { const { data } = await api.post("/admin/affiliate/preview", { custom_html: f.custom_html }); setPreviewHtml(data.html); }
      catch (e) { toast.error(apiErr(e)); }
    } else setPreviewHtml("");
  };

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Campaign name is required.");
    if (f.placements.length === 0) return toast.error("Select at least one placement.");
    setBusy(true);
    try {
      const payload = { ...f, priority: Number(f.priority) || 1, weight: Number(f.weight) || 1, max_impressions: Number(f.max_impressions) || 0 };
      if (initial?.id) await api.put(`/admin/affiliate/${initial.id}`, payload);
      else await api.post("/admin/affiliate", payload);
      toast.success(initial?.id ? "Campaign updated" : "Campaign created");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const L = ({ children }) => <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block mb-1">{children}</label>;
  const inputCls = "w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pti-red";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="affiliate-form-modal">
      <div className="bg-white w-full max-w-3xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-pti-black sticky top-0 bg-white">
          <h2 className="font-display text-2xl font-bold">{initial?.id ? "Edit" : "Create"} Affiliate Campaign</h2>
          <button onClick={onClose} data-testid="affiliate-form-close" aria-label="Close"><X size={22} /></button>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-5">
          <div><L>Campaign name *</L><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="camp-name" className={inputCls} /></div>
          <div><L>Advertiser / brand name</L><input value={f.partner} onChange={(e) => set("partner", e.target.value)} data-testid="camp-partner" className={inputCls} /></div>

          <div className="sm:col-span-2">
            <L>Creative type</L>
            <div className="flex gap-2">
              {[["native", "Native card"], ["image", "Image / banner"], ["html", "Partner HTML"]].map(([val, lbl]) => (
                <button key={val} type="button" onClick={() => set("creative_type", val)} data-testid={`camp-type-${val}`}
                  className={`px-4 py-2 text-sm font-bold border transition-colors ${f.creative_type === val ? "bg-pti-black text-white border-pti-black" : "border-gray-300 text-gray-600 hover:border-pti-black"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2"><L>Destination URL</L><input value={f.href} onChange={(e) => set("href", e.target.value)} data-testid="camp-href" placeholder="https://www.advertiser.com" className={inputCls} /></div>

          {f.creative_type === "native" && (
            <>
              <div><L>Ad label</L><input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid="camp-native-label" placeholder="ADVERTISEMENT" className={inputCls} /></div>
              <div>
                <L>Display category</L>
                <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="camp-native-category" className={inputCls}>
                  <option value="">None</option>
                  {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><L>Headline</L><input value={f.headline} onChange={(e) => set("headline", e.target.value)} data-testid="camp-native-headline" placeholder="Software that actually ships" className={inputCls} /></div>
              <div className="sm:col-span-2"><L>Description</L><textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="camp-native-description" rows={2} className={inputCls} /></div>
              <div><label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block mb-1 whitespace-nowrap">Button text</label><input value={f.cta} onChange={(e) => set("cta", e.target.value)} data-testid="camp-native-cta" placeholder="See our work →" className={inputCls} /></div>
              <div>
                <FileUpload label="Logo (optional)" accept="image/*" testid="camp-native-logo-upload" onUploaded={(url) => set("logo", url)} hint="Small square logo · or paste URL →" />
                <input value={f.logo} onChange={(e) => set("logo", e.target.value)} data-testid="camp-native-logo-url" placeholder="Logo URL" className={`${inputCls} mt-2`} />
              </div>
            </>
          )}

          {f.creative_type !== "html" && (
            <>
              <div><FileUpload label={f.creative_type === "native" ? "Card image (optional)" : "Creative image (upload)"} accept="image/*" testid="camp-image-upload" onUploaded={(url) => set("image", url)} hint="PNG/JPG. Or paste an approved URL →" /></div>
              <div>
                <L>Approved image URL</L><input value={f.image} onChange={(e) => set("image", e.target.value)} data-testid="camp-image-url" placeholder="https://cdn.partner.com/creative.jpg" className={inputCls} />
                <div className="mt-2"><L>Image alt text</L><input value={f.image_alt} onChange={(e) => set("image_alt", e.target.value)} data-testid="camp-image-alt" className={inputCls} /></div>
              </div>
            </>
          )}

          {f.creative_type === "html" && (
            <div className="sm:col-span-2">
              <L>Restricted affiliate HTML (admin-only · sanitized to safe &lt;a&gt;/&lt;img&gt; only)</L>
              <textarea value={f.custom_html} onChange={(e) => set("custom_html", e.target.value)} data-testid="camp-html" rows={3} className={inputCls} placeholder="Paste partner ad code. Scripts, iframes, trackers and pop-ups are stripped automatically." />
              <button onClick={doPreview} data-testid="camp-preview-html" className="mt-2 flex items-center gap-1 text-xs font-bold px-3 py-1.5 border border-pti-black hover:bg-pti-black hover:text-white transition-colors"><Eye size={13} /> Preview sanitized ad</button>
            </div>
          )}

          <div className="sm:col-span-2">
            <L>Placements — the red block shows where your ad appears</L>
            <div className="grid sm:grid-cols-2 gap-2 border border-gray-200 p-3">
              {placements.map((p) => {
                const vis = PLACEMENT_VIS[p.key] || {};
                return (
                  <label key={p.key} className="flex items-center gap-2.5 text-sm cursor-pointer p-1.5 rounded hover:bg-gray-50" data-testid={`camp-placement-${p.key}`} title={vis.d || p.label}>
                    <input type="checkbox" checked={f.placements.includes(p.key)} onChange={() => toggleArr("placements", p.key)} className="accent-pti-red shrink-0" />
                    <PlacementThumb pkey={p.key} />
                    <span className="flex-1 leading-tight">
                      <span className="block font-semibold text-gray-800">{p.label}</span>
                      {vis.d && <span className="block text-[11px] text-gray-400 mt-0.5">{vis.d}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <L>Category targeting (show only in)</L>
            <div className="border border-gray-200 p-2 max-h-32 overflow-y-auto">
              {CATEGORY_OPTIONS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-xs cursor-pointer py-0.5"><input type="checkbox" checked={f.eligible_categories.includes(c)} onChange={() => toggleArr("eligible_categories", c)} className="accent-pti-red" />{c}</label>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">None selected = all categories.</p>
          </div>
          <div>
            <L>Exclude categories / sensitive content</L>
            <div className="border border-gray-200 p-2 max-h-32 overflow-y-auto">
              {CATEGORY_OPTIONS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-xs cursor-pointer py-0.5"><input type="checkbox" checked={f.exclude_categories.includes(c)} onChange={() => toggleArr("exclude_categories", c)} className="accent-pti-red" />{c}</label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer mt-2" data-testid="camp-block-sensitive"><input type="checkbox" checked={f.block_sensitive} onChange={(e) => set("block_sensitive", e.target.checked)} className="accent-pti-red" /> Keep off sensitive content</label>
          </div>

          <div><L>Start date</L><input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="camp-start" className={inputCls} /></div>
          <div><L>End date</L><input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="camp-end" className={inputCls} /></div>

          <div><L>Status</L><select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="camp-status" className={inputCls}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><L>Ad label</L><input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid="camp-label" className={inputCls} /></div>

          <div><L>Priority (1 = highest)</L><input type="number" min="1" value={f.priority} onChange={(e) => set("priority", e.target.value)} data-testid="camp-priority" className={inputCls} /></div>
          <div><L>Rotation weight</L><input type="number" min="1" value={f.weight} onChange={(e) => set("weight", e.target.value)} data-testid="camp-weight" className={inputCls} /></div>

          <div><L>Impression cap (display limit · 0 = unlimited)</L><input type="number" min="0" value={f.max_impressions} onChange={(e) => set("max_impressions", e.target.value)} data-testid="camp-cap" className={inputCls} /></div>
          <div><L>Link target</L><select value={f.target} onChange={(e) => set("target", e.target.value)} data-testid="camp-target" className={inputCls}><option value="_blank">_blank</option><option value="_top">_top</option><option value="_self">_self</option></select></div>

          <div className="sm:col-span-2"><L>Notes</L><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="camp-notes" rows={2} className={inputCls} /></div>

          {(previewHtml || f.image || (f.creative_type === "native" && f.headline)) && (
            <div className="sm:col-span-2" data-testid="camp-preview">
              <L>Preview</L>
              {f.creative_type === "native" ? (
                <div className="bg-white border border-gray-200 max-w-sm group">
                  <div className="flex items-center justify-between px-3 pt-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-pti-red inline-block" /> {f.label || "Advertisement"}</span>
                    {f.category && <span className="text-[10px] text-gray-400">{f.category}</span>}
                  </div>
                  {f.image && <div className="aspect-video overflow-hidden bg-gray-100 mt-2"><img src={f.image} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="p-3">
                    {(f.logo || f.partner) && <div className="flex items-center gap-2 mb-1">{f.logo && <img src={f.logo} alt="" className="w-5 h-5 object-contain" />}{f.partner && <span className="text-xs font-bold text-gray-700">{f.partner}</span>}</div>}
                    <h4 className="font-display font-bold text-base leading-tight">{f.headline}</h4>
                    {f.description && <p className="text-sm text-gray-600 mt-1">{f.description}</p>}
                    {f.cta && <span className="inline-block mt-3 bg-pti-red text-white text-xs font-bold uppercase tracking-widest px-4 py-2">{f.cta}</span>}
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-3 max-w-sm">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1 mb-2"><span className="w-1.5 h-1.5 bg-pti-red inline-block" /> {f.label}</div>
                  {previewHtml
                    ? <div className="[&_img]:max-w-full [&_img]:h-auto [&_img]:mx-auto" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    : <img src={f.image} alt={f.image_alt} style={{ maxWidth: "100%", height: "auto" }} className="block mx-auto rounded" />}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold border border-gray-300 hover:border-pti-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="camp-save" className="flex items-center gap-2 px-5 py-2 text-sm font-bold bg-pti-red text-white hover:bg-pti-black transition-colors disabled:opacity-50"><Save size={15} /> {busy ? "Saving…" : "Save campaign"}</button>
        </div>
      </div>
    </div>
  );
}
